import React from 'react';
import { Bed, Bath, Car, Maximize2, MapPin, ShieldCheck, ArrowRight, MessageCircle, Zap, Tag } from 'lucide-react';
import { useProperties } from '../context/PropertyContext';

export const PropertyCard = ({ property, onSelectProperty }) => {
  const { getWhatsAppLink, recordWhatsAppClick } = useProperties();

  const formatPrice = (price, currency = 'MXN') => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(price || 0);
  };

  const handleCardClick = () => {
    if (onSelectProperty) {
      onSelectProperty(property);
    }
  };

  const handleWhatsAppClick = (e) => {
    e.stopPropagation();
    recordWhatsAppClick(property.id);
  };

  const isRent = (property.operationType || property.operation) === 'Renta' || (property.operationType || property.operation) === 'renta';
  const mainImage = property.images && property.images.length > 0
    ? property.images[0]
    : 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80';

  const neighborhood = property.location?.neighborhood || property.location?.colonia || '';
  const city = property.location?.city || 'Hidalgo del Parral';
  const locationText = neighborhood ? `${neighborhood}, ${city}` : city;

  const isLand = (property.propertyType || property.type) === 'Terreno';

  const effectivePrice = Number(property.priceMXN || property.price || 0);
  const originalPrice = Number(property.originalPriceMXN || property.originalPrice || 0);
  const hasDiscount = Boolean(property.hasDiscount || (originalPrice > 0 && originalPrice > effectivePrice));
  const discountPct = hasDiscount && originalPrice > 0
    ? Math.round(((originalPrice - effectivePrice) / originalPrice) * 100)
    : 0;
  const savings = hasDiscount && originalPrice > effectivePrice ? originalPrice - effectivePrice : 0;

  const waMessage = `Hola Lic. Edgar Molina, deseo información de la propiedad: ${property.title} (${locationText}).`;

  return (
    <article className="property-card" onClick={handleCardClick} aria-label={`Propiedad: ${property.title}`}>
      {/* 16:9 Aspect Ratio Image Container */}
      <div className="property-card-image-wrap" style={{ position: 'relative' }}>
        <img
          src={mainImage}
          alt={`Fotografía: ${property.title} en ${locationText}`}
          className="property-card-image"
          loading="lazy"
        />

        {/* Top Badges */}
        <div className="property-badge-top-left">
          <span className={`badge ${isRent ? 'badge-gold' : 'badge-navy'}`}>
            {isRent ? 'Renta' : 'Venta'}
          </span>
          {property.featured && (
            <span className="badge badge-gold">
              Destacada
            </span>
          )}
        </div>

        <div className="property-badge-top-right">
          <span className="property-type-tag">
            {property.propertyType || property.type || 'Inmueble'}
          </span>
        </div>

        {/* PROMINENT DISCOUNT BANNER RIBBON OVER IMAGE */}
        {hasDiscount && discountPct > 0 && (
          <div
            style={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              right: '0',
              background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)',
              color: '#ffffff',
              padding: '6px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 800,
              fontSize: '0.82rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
              zIndex: 3
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>OPORTUNIDAD DE PRECIO</span>
            </div>
            <span style={{
              backgroundColor: '#ffffff',
              color: '#dc2626',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.78rem',
              fontWeight: 900
            }}>
              -{discountPct}% OFF
            </span>
          </div>
        )}
      </div>

      {/* Content Body */}
      <div className="property-card-content">
        {/* Price Section with Discount Support */}
        {hasDiscount && originalPrice > effectivePrice ? (
          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              <span style={{
                fontSize: '0.88rem',
                color: '#64748b',
                textDecoration: 'line-through',
                fontWeight: 600
              }}>
                {formatPrice(originalPrice, property.currency)}
              </span>
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 800,
                color: '#b91c1c',
                backgroundColor: '#fef2f2',
                padding: '2px 6px',
                borderRadius: '4px',
                border: '1px solid #fecaca'
              }}>
                Ahorras {formatPrice(savings, property.currency)}
              </span>
            </div>
            <div className="property-card-price" style={{ color: '#b91c1c', fontSize: '1.45rem', margin: 0 }}>
              {formatPrice(effectivePrice, property.currency)}
              {isRent && <span className="property-card-price-period">/mes</span>}
            </div>
          </div>
        ) : (
          <div className="property-card-price">
            {formatPrice(effectivePrice, property.currency)}
            {isRent && <span className="property-card-price-period">/mes</span>}
          </div>
        )}

        {/* Title */}
        <h3 className="property-card-title" title={property.title}>
          {property.title}
        </h3>

        {/* Location */}
        <div className="property-card-location">
          <MapPin size={15} color="var(--color-primary)" />
          <span>{locationText}</span>
        </div>

        {/* Features Grid with Clear Icons (Conditional by Type) */}
        {isLand ? (
          <div className="property-features-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="property-feature-item" title={`Superficie: ${property.features?.landSizeM2 || property.specs?.landArea || 0} m²`}>
              <Maximize2 size={17} className="property-feature-icon" />
              <span className="property-feature-value">{property.features?.landSizeM2 || property.specs?.landArea || 0}m²</span>
              <span className="property-feature-label">Terreno Total</span>
            </div>

            <div className="property-feature-item" title="Servicios disponibles">
              <Zap size={17} className="property-feature-icon" />
              <span className="property-feature-value">
                {property.features?.services?.length > 0 ? `${property.features.services.length} Serv.` : 'Por verificar'}
              </span>
              <span className="property-feature-label">Servicios</span>
            </div>
          </div>
        ) : (
          <div className="property-features-grid">
            {property.features?.hasBedrooms && (
              <div className="property-feature-item" title={`${property.features.bedrooms} Recámaras`}>
                <Bed size={17} className="property-feature-icon" />
                <span className="property-feature-value">{property.features.bedrooms}</span>
                <span className="property-feature-label">Recs</span>
              </div>
            )}

            {property.features?.hasBathrooms && (
              <div className="property-feature-item" title={`${property.features.fullBathrooms || property.specs?.bathrooms || 0} Baños Completos${Number(property.features?.halfBathrooms) > 0 ? ` + ${property.features.halfBathrooms} Medios Baños` : ''}`}>
                <Bath size={17} className="property-feature-icon" />
                <span className="property-feature-value">
                  {property.features.fullBathrooms || property.specs?.bathrooms || 1}
                  {Number(property.features?.halfBathrooms) > 0 && (
                    <small style={{ fontSize: '0.68em', fontWeight: 800, color: 'var(--color-primary-light)' }}>
                      {' '}(+{Number(property.features.halfBathrooms) > 1 ? `${property.features.halfBathrooms} ` : ''}½)
                    </small>
                  )}
                </span>
                <span className="property-feature-label">Baños</span>
              </div>
            )}

            {property.features?.hasParking && (
              <div className="property-feature-item" title={`${property.features.parkingSpaces} Estacionamientos`}>
                <Car size={17} className="property-feature-icon" />
                <span className="property-feature-value">{property.features.parkingSpaces}</span>
                <span className="property-feature-label">Autos</span>
              </div>
            )}

            <div className="property-feature-item" title={`Superficie: ${property.features?.buildSizeM2 || property.features?.landSizeM2 || property.specs?.buildArea || property.specs?.landArea || 0} m²`}>
              <Maximize2 size={17} className="property-feature-icon" />
              <span className="property-feature-value">
                {(property.features?.buildSizeM2 || property.specs?.buildArea) > 0
                  ? `${property.features?.buildSizeM2 || property.specs?.buildArea}m²`
                  : `${property.features?.landSizeM2 || property.specs?.landArea || 0}m²`}
              </span>
              <span className="property-feature-label">
                {(property.features?.buildSizeM2 || property.specs?.buildArea) > 0 ? 'Const.' : 'Terr.'}
              </span>
            </div>
          </div>
        )}

        {/* Card Action Buttons */}
        <div className="property-card-actions">
          <button
            type="button"
            className="btn-view-property"
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
          >
            <span>Ver Ficha Técnica</span>
            <ArrowRight size={16} />
          </button>

          <a
            href={getWhatsAppLink(waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-quick-whatsapp"
            onClick={handleWhatsAppClick}
            aria-label={`Preguntar por WhatsApp sobre ${property.title}`}
            title="Preguntar por WhatsApp"
          >
            <MessageCircle size={18} />
          </a>
        </div>
      </div>
    </article>
  );
};
